import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, Clock, CheckCircle, Send, RotateCcw, Archive, Trash2 } from 'lucide-react';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import DocumentHeader from './document/DocumentHeader';
import DocumentActions from './document/DocumentActions';
import DocumentItems from './document/DocumentItems';
import DocumentSidebar from './document/DocumentSidebar';
import { StatusChangeDialog, DeleteDialog, AssignDialog, UploadRevisionDialog, AddItemDialog } from './document/DocumentDialogs';
import AlfrescoExportDialog from './document/AlfrescoExportDialog';
import DocumentPremissas from './document/DocumentPremissas';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_CONFIG = {
  'Rascunho': { color: 'bg-gray-500', icon: FileText, label: 'Rascunho' },
  'Em Revisão': { color: 'bg-yellow-500', icon: Clock, label: 'Em Revisão' },
  'Aprovado': { color: 'bg-blue-500', icon: CheckCircle, label: 'Aprovado' },
  'Finalizado': { color: 'bg-green-600', icon: CheckCircle, label: 'Finalizado' },
  'Arquivado': { color: 'bg-red-500', icon: Archive, label: 'Arquivado' },
  'Excluído': { color: 'bg-red-800', icon: Trash2, label: 'Excluído' }
};

const TRANSITIONS = {
  'Rascunho': [{ status: 'Em Revisão', label: 'Enviar para Revisão', icon: Send, color: 'bg-yellow-500 hover:bg-yellow-600' }],
  'Em Revisão': [
    { status: 'Aprovado', label: 'Aprovar', icon: CheckCircle, color: 'bg-blue-600 hover:bg-blue-700' },
    { status: 'Rascunho', label: 'Devolver para Rascunho', icon: RotateCcw, color: 'bg-gray-500 hover:bg-gray-600' }
  ],
  'Aprovado': [
    { status: 'Finalizado', label: 'Finalizar', icon: CheckCircle, color: 'bg-green-600 hover:bg-green-700' },
    { status: 'Em Revisão', label: 'Reabrir Revisão', icon: RotateCcw, color: 'bg-yellow-500 hover:bg-yellow-600' }
  ],
  'Finalizado': [{ status: 'Arquivado', label: 'Arquivar', icon: Archive, color: 'bg-red-500 hover:bg-red-600' }],
  'Arquivado': []
};

const DocumentDetail = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();

  // Data state
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [versions, setVersions] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI state
  const [exporting, setExporting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editVars, setEditVars] = useState({});
  const [newVarName, setNewVarName] = useState('');
  const [newVarValue, setNewVarValue] = useState('');
  const [editItems, setEditItems] = useState(false);
  const [editedItems, setEditedItems] = useState([]);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [addingItem, setAddingItem] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [statusDialog, setStatusDialog] = useState(null);
  const [statusComment, setStatusComment] = useState('');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [newItemQty, setNewItemQty] = useState(1);
  const [alfrescoOpen, setAlfrescoOpen] = useState(false);
  const [alfrescoConnected, setAlfrescoConnected] = useState(false);

  // Data loaders
  const loadDocument = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/documents/${documentId}`, { withCredentials: true });
      setData(response.data);
      setEditVars(response.data.version?.variables_filled || {});
    } catch (error) {
      console.error('Failed to load document:', error);
      toast.error('Erro ao carregar documento');
      navigate('/documents');
    } finally {
      setLoading(false);
    }
  }, [documentId, navigate]);

  const loadHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/documents/${documentId}/history`, { withCredentials: true });
      setHistory(response.data);
    } catch (error) {
      console.error('Failed to load history:', error);
    }
  }, [documentId]);

  const loadVersions = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/documents/${documentId}/versions`, { withCredentials: true });
      setVersions(response.data);
    } catch (error) {
      console.error('Failed to load versions:', error);
    }
  }, [documentId]);

  const loadUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/users`, { withCredentials: true });
      setUsers(response.data.filter(u => u.is_active));
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  }, []);

  const loadCurrentUser = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/me`, { withCredentials: true });
      setCurrentUser(res.data);
    } catch (error) {
      console.error('Failed to load current user:', error);
    }
  }, []);

  const loadProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/products`, { withCredentials: true });
      setProducts(res.data);
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  }, []);

  const checkAlfresco = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/alfresco/status`, { withCredentials: true });
      setAlfrescoConnected(res.data.connected === true);
    } catch {
      setAlfrescoConnected(false);
    }
  }, []);

  useEffect(() => {
    loadDocument();
    loadHistory();
    loadUsers();
    loadVersions();
    loadCurrentUser();
    checkAlfresco();
  }, [loadDocument, loadHistory, loadUsers, loadVersions, loadCurrentUser, checkAlfresco]);

  // Action handlers
  const handleExport = useCallback(async (format = 'docx') => {
    setExporting(true);
    try {
      const token = localStorage.getItem('session_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/documents/${documentId}/export?format=${format}`, {
        method: 'POST', credentials: 'include',
        headers, body: JSON.stringify({})
      });
      if (!res.ok) {
        let msg = `Erro ao exportar .${format}`;
        try { const err = await res.json(); msg = err.detail || msg; } catch (e) { console.error('Export error parse failed:', e); }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${data.document.title}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exportado como .${format} com sucesso`);
    } catch (error) {
      toast.error(error.message || `Erro ao exportar .${format}`);
    } finally {
      setExporting(false);
    }
  }, [documentId, data]);

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/documents/${documentId}`, { withCredentials: true });
      toast.success('Documento excluído');
      navigate('/documents');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.patch(`${API_URL}/documents/${documentId}/status`, { status: newStatus, comment: statusComment }, { withCredentials: true });
      toast.success(`Status alterado para ${newStatus}`);
      setStatusDialog(null);
      setStatusComment('');
      loadDocument();
      loadHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar status');
    }
  };

  const handleAssign = async () => {
    try {
      const assignTo = selectedAssignee === 'none' ? null : (selectedAssignee || null);
      await axios.put(`${API_URL}/documents/${documentId}/assign`, { assigned_to: assignTo }, { withCredentials: true });
      toast.success(assignTo ? 'Responsável atribuído' : 'Atribuição removida');
      setAssignDialogOpen(false);
      loadDocument();
      loadHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atribuir');
    }
  };

  const handleSaveVariables = async () => {
    try {
      await axios.put(`${API_URL}/documents/${documentId}`, { variables: editVars }, { withCredentials: true });
      toast.success('Variáveis salvas');
      setEditMode(false);
      loadDocument();
    } catch (error) {
      toast.error('Erro ao salvar');
    }
  };

  const handleAddCustomVariable = () => {
    const name = newVarName.trim().toUpperCase().replace(/[^A-Z0-9_]/g, '_');
    if (!name) { toast.error('Informe o nome da variável'); return; }
    if (editVars[name] !== undefined) { toast.error('Variável já existe'); return; }
    setEditVars({ ...editVars, [name]: newVarValue });
    setNewVarName('');
    setNewVarValue('');
    toast.success(`Variável {{${name}}} adicionada`);
  };

  const handleRemoveVariable = (key) => {
    const updated = { ...editVars };
    delete updated[key];
    setEditVars(updated);
  };

  // Item handlers
  const handleEditItems = () => {
    setEditedItems(data.items.map(item => ({
      item_id: item.item_id, product_name: item.product_name,
      quantity: item.quantity, unit_price_applied: item.unit_price_applied || 0,
      descricao_resumida: item.descricao_resumida || item.product_description || '',
      unidade: item.unidade || 'un'
    })));
    setEditItems(true);
  };

  const handleUpdateEditedItem = (itemId, field, value) => {
    setEditedItems(prev => prev.map(it => it.item_id === itemId ? { ...it, [field]: value } : it));
  };

  const handleSaveItems = async () => {
    try {
      await axios.put(`${API_URL}/documents/${documentId}/items`, {
        items: editedItems.map(item => ({
          item_id: item.item_id, quantity: parseFloat(item.quantity) || 0,
          unit_price_override: parseFloat(item.unit_price_applied) || 0,
          descricao_resumida: item.descricao_resumida, unidade: item.unidade
        }))
      }, { withCredentials: true });
      toast.success('Itens atualizados');
      setEditItems(false);
      loadDocument();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar itens');
    }
  };

  const handleLoadPrices = async () => {
    setLoadingPrices(true);
    try {
      const response = await axios.post(`${API_URL}/documents/${documentId}/load-prices`, {}, { withCredentials: true });
      if (response.data.updated_count > 0) {
        toast.success(`${response.data.updated_count} item(s) atualizado(s) com preços da tabela`);
      } else {
        toast.info('Nenhum preço encontrado na tabela de preços para os itens atuais');
      }
      loadDocument();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar preços');
    } finally {
      setLoadingPrices(false);
    }
  };

  const handleOpenAddItem = () => {
    loadProducts();
    setSelectedProduct(null);
    setNewItemQty(1);
    setProductSearch('');
    setAddItemOpen(true);
  };

  const handleAddItem = async () => {
    if (!selectedProduct) return;
    setAddingItem(true);
    try {
      await axios.post(`${API_URL}/documents/${documentId}/items/add`, {
        product_id: selectedProduct.product_id, quantity: parseFloat(newItemQty) || 1,
      }, { withCredentials: true });
      toast.success(`"${selectedProduct.name}" adicionado ao documento`);
      setAddItemOpen(false);
      setSelectedProduct(null);
      loadDocument();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar item');
    } finally {
      setAddingItem(false);
    }
  };

  const handleDeleteItem = async (itemId, productName) => {
    if (!window.confirm(`Remover "${productName}" do documento?`)) return;
    try {
      await axios.delete(`${API_URL}/documents/${documentId}/items/${itemId}`, { withCredentials: true });
      toast.success('Item removido');
      loadDocument();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover item');
    }
  };

  const handleUploadRevision = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      await axios.post(`${API_URL}/documents/${documentId}/upload-revision`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Nova versão enviada com sucesso');
      setUploadDialogOpen(false);
      setUploadFile(null);
      loadDocument();
      loadHistory();
      loadVersions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar arquivo');
    } finally {
      setUploading(false);
    }
  };

  // Render
  if (loading) {
    return <div className="flex justify-center p-12"><div className="w-12 h-12 border-4 border-[#AF2628] border-t-transparent rounded-full animate-spin"></div></div>;
  }
  if (!data) return null;

  const { document: doc, version, items, client, assignee } = data;
  const statusCfg = STATUS_CONFIG[doc.status] || STATUS_CONFIG['Rascunho'];
  const transitions = TRANSITIONS[doc.status] || [];
  const isAdmin = currentUser?.role === 'Administrador';
  const isExcluded = doc.status === 'Excluído';
  const canEdit = !isExcluded && doc.status !== 'Finalizado' && doc.status !== 'Arquivado';

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="document-detail">
      <DocumentHeader doc={doc} client={client} statusCfg={statusCfg} isExcluded={isExcluded} isAdmin={isAdmin} onBack={() => navigate('/documents')} />

      <DocumentActions
        doc={doc} transitions={transitions} isExcluded={isExcluded} isAdmin={isAdmin}
        canEdit={canEdit} exporting={exporting} editMode={editMode}
        onStatusDialog={setStatusDialog} onEditVars={() => setEditMode(!editMode)}
        onExport={handleExport} onDelete={() => setDeleteOpen(true)} onUpload={() => setUploadDialogOpen(true)}
        onAlfrescoExport={() => setAlfrescoOpen(true)} alfrescoConnected={alfrescoConnected}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Client Info */}
          <div className="bg-white border border-[#E5E5EA] rounded-xl p-5">
            <h3 className="font-semibold text-[#111111] mb-3">Dados do Cliente</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-[#6E6E73]">Razão Social:</span><p className="font-medium">{client?.razao_social || 'N/A'}</p></div>
              <div><span className="text-[#6E6E73]">CNPJ:</span><p className="font-medium">{client?.cnpj || 'N/A'}</p></div>
              <div><span className="text-[#6E6E73]">Tipo:</span><p className="font-medium">{client?.tipo || 'N/A'} {client?.esfera ? `- ${client.esfera}` : ''} {client?.poder ? `/ ${client.poder}` : ''}</p></div>
              <div><span className="text-[#6E6E73]">Cidade/UF:</span><p className="font-medium">{client?.cidade || ''}{client?.uf ? `/${client.uf}` : ''}</p></div>
            </div>
          </div>

          <DocumentPremissas documentId={documentId} canEdit={canEdit} />

          <DocumentItems
            items={items} editItems={editItems} editedItems={editedItems}
            canEdit={canEdit} loadingPrices={loadingPrices}
            onEditItems={handleEditItems} onSaveItems={handleSaveItems}
            onCancelEdit={() => setEditItems(false)} onLoadPrices={handleLoadPrices}
            onOpenAddItem={handleOpenAddItem} onDeleteItem={handleDeleteItem}
            onUpdateEditedItem={handleUpdateEditedItem}
          />

          {/* Variables Editor */}
          {editMode && (
            <div className="bg-white border border-[#E5E5EA] rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-[#111111]">Editar Variáveis</h3>
                <Button size="sm" className="bg-[#AF2628] hover:bg-[#961B22]" onClick={handleSaveVariables} data-testid="save-variables-button">Salvar Variáveis</Button>
              </div>
              <div className="bg-[#F7F7F8] rounded-lg p-3 mb-4">
                <Label className="text-xs font-medium text-[#6E6E73] mb-2 block">Adicionar Variável Personalizada</Label>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Input value={newVarName} onChange={(e) => setNewVarName(e.target.value)} placeholder="Nome (ex: PRAZO_GARANTIA)" className="text-sm" data-testid="new-variable-name-input" />
                  </div>
                  <div className="flex-1">
                    <Input value={newVarValue} onChange={(e) => setNewVarValue(e.target.value)} placeholder="Valor" className="text-sm" data-testid="new-variable-value-input" />
                  </div>
                  <Button size="sm" variant="outline" onClick={handleAddCustomVariable} data-testid="add-variable-button">+ Adicionar</Button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(editVars).map(([key, value]) => (
                  <div key={key} className="group relative">
                    <Label className="text-xs text-[#6E6E73] flex items-center justify-between">
                      <span>{key}</span>
                      <button type="button" onClick={() => handleRemoveVariable(key)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 text-xs transition-opacity" title="Remover variável">remover</button>
                    </Label>
                    <Textarea value={value || ''} onChange={(e) => setEditVars({ ...editVars, [key]: e.target.value })} className="text-sm min-h-[38px] resize-y" rows={1} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DocumentSidebar
          doc={doc} client={client} assignee={assignee} versions={versions}
          history={history} documentId={documentId}
          updatedByInfo={data?.updated_by_info} createdByInfo={data?.created_by_info}
          onAssign={() => { setSelectedAssignee(doc.assigned_to || ''); setAssignDialogOpen(true); }}
        />
      </div>

      {/* Dialogs */}
      <StatusChangeDialog open={!!statusDialog} statusDialog={statusDialog} docStatus={doc.status} statusComment={statusComment} onCommentChange={setStatusComment} onConfirm={handleStatusChange} onClose={() => setStatusDialog(null)} />
      <DeleteDialog open={deleteOpen} docTitle={doc.title} onConfirm={handleDelete} onClose={setDeleteOpen} />
      <AssignDialog open={assignDialogOpen} users={users} selectedAssignee={selectedAssignee} onSelect={setSelectedAssignee} onConfirm={handleAssign} onClose={setAssignDialogOpen} />
      <UploadRevisionDialog open={uploadDialogOpen} doc={doc} uploadFile={uploadFile} uploading={uploading} onFileChange={setUploadFile} onConfirm={handleUploadRevision} onClose={(v) => { setUploadDialogOpen(v); if (!v) setUploadFile(null); }} />
      <AddItemDialog open={addItemOpen} products={products} productSearch={productSearch} selectedProduct={selectedProduct} newItemQty={newItemQty} addingItem={addingItem} onSearchChange={setProductSearch} onSelectProduct={setSelectedProduct} onQtyChange={setNewItemQty} onConfirm={handleAddItem} onClose={setAddItemOpen} />
      <AlfrescoExportDialog open={alfrescoOpen} onClose={setAlfrescoOpen} documentId={documentId} docTitle={doc.title} onExported={() => loadHistory()} />
    </div>
  );
};

export default DocumentDetail;
